import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import TypographyOverride from "../components/TypographyOverride";

const DEFAULT_CATEGORIES = ["Branding", "Film", "Social", "Digital"];

export default function Work({ settings }) {
  const [works, setWorks] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api.get("/works").then(({ data }) => setWorks(data));
  }, []);

  // Categories come from admin (settings.work_categories) if set, else fallback.
  // "All" is always prepended as the reset option.
  const customCats = (settings?.work_categories || []).filter(Boolean);
  const CATEGORIES = ["All", ...(customCats.length > 0 ? customCats : DEFAULT_CATEGORIES)];

  const filtered = useMemo(() => {
    if (active === "All") return works;
    return works.filter((w) => w.category === active);
  }, [active, works]);

  // Heading rendering: last word becomes the gold italic accent.
  // E.g. "The archive." → "The " + <span>archive</span> + "."
  const headingRaw = settings?.work_heading || "The archive.";
  const headingParts = useMemo(() => {
    const trimmed = headingRaw.trim();
    const trailing = trimmed.endsWith(".") ? "." : "";
    const core = trailing ? trimmed.slice(0, -1) : trimmed;
    const idx = core.lastIndexOf(" ");
    if (idx === -1) return { lead: "", accent: core, trailing };
    return { lead: core.slice(0, idx + 1), accent: core.slice(idx + 1), trailing };
  }, [headingRaw]);

  const description =
    settings?.work_description ||
    "A quiet collection of projects made with care. Each one partnered with a founder or creative director who wanted something that would last.";

  return (
    <main className="work-listing-page pt-32 md:pt-40 pb-24">
      {/* Typography overrides — the /work grid has its own zone set (page
         title, filter pills, card title, card meta) editable in
         Typography → Works Grid. */}
      <TypographyOverride pageId="work" scopeSelector=".work-listing-page" prefix="wl" />
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 mb-16 md:mb-24">
        <Reveal>
          <p className="overline mb-6">Work · {works.length} projects</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 data-testid="work-page-title" className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
            {headingParts.lead}
            <span className="font-serif-accent text-[#C9A050]">{headingParts.accent}</span>
            {headingParts.trailing}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p data-testid="work-page-description" className="cs-body cs-body-medium mt-8 md:mt-10 text-[#A1A1AA] max-w-xl font-light whitespace-pre-line">
            {description}
          </p>
        </Reveal>
      </section>

      {/* Filters */}
      <section className="px-6 md:px-12 lg:px-20 mb-12 md:mb-16">
        <div className="flex flex-wrap gap-3 md:gap-4 border-b border-white/5 pb-6">
          {CATEGORIES.map((c) => {
            const on = c === active;
            return (
              <button
                key={c}
                data-testid={`work-filter`}
                data-filter={c.toLowerCase()}
                onClick={() => setActive(c)}
                className={`overline px-4 py-2 border transition-all ${
                  on
                    ? "border-[#C9A050] text-[#C9A050] bg-[#C9A050]/5"
                    : "border-white/10 text-[#8A8A93] hover:text-white hover:border-white/30"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section data-testid="work-grid" className="px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-16 md:gap-y-24">
        {filtered.map((w, idx) => {
          const vertical = w.orientation === "vertical";
          // Vertical works take a narrower column; horizontal take wider
          const pattern = vertical
            ? [
                "md:col-span-4",
                "md:col-span-4 md:mt-20",
                "md:col-span-4",
              ]
            : [
                "md:col-span-7",
                "md:col-span-5 md:mt-24",
                "md:col-span-5",
                "md:col-span-7 md:mt-16",
                "md:col-span-6",
                "md:col-span-6 md:mt-20",
              ];
          const span = pattern[idx % pattern.length];
          const frame = vertical ? "aspect-[9/16]" : "aspect-[4/5]";
          const href = w.slug ? `/work/${w.slug}` : `/work`;
          return (
            <Reveal key={w.id} delay={(idx % 3) * 0.08} className={span}>
              <Link
                to={href}
                data-testid={`work-item-${w.id}`}
                data-orientation={vertical ? "vertical" : "horizontal"}
                data-cursor="View"
                className="group block w-full text-left"
              >
                <div className={`relative overflow-hidden ${frame} bg-[#121214]`}>
                  <picture>
                    {w.cover_image_mobile && (
                      <source media="(max-width: 767px)" srcSet={w.cover_image_mobile} />
                    )}
                    <img
                      src={w.cover_image}
                      alt={w.title}
                      loading="lazy"
                      decoding="async"
                      width={w.orientation === "vertical" ? 1080 : 1600}
                      height={w.orientation === "vertical" ? 1920 : 900}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6 md:p-8">
                    <span className="overline text-[#C9A050]">View project →</span>
                  </div>
                </div>
                <div className="mt-6 flex items-end justify-between gap-6">
                  <div>
                    <p data-testid="work-card-meta" className="overline mb-3">
                      {w.category} <span className="mx-2 opacity-40">/</span> {w.year}
                    </p>
                    <h3 data-testid="work-card-title" className="font-display text-2xl md:text-3xl tracking-tight">{w.title}</h3>
                    {w.client && <p className="text-[#8A8A93] text-sm mt-2">For {w.client}</p>}
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </section>

      {filtered.length === 0 && (
        <div className="px-6 md:px-12 text-center py-32">
          <p className="overline text-[#8A8A93]">No projects in this category yet</p>
        </div>
      )}
    </main>
  );
}
